import { KEYBOARD_LAYOUTS } from "./keyCodeMapper";

/**
 * DEEP80 MAX：虚拟键盘为 SMART875(84) 矩阵（含占位 0 列），
 * 星闪 SDK 自定义灯网格为紧凑列（无 Esc 后、L-Shift 后等占位列）。
 */

/** DEEP80 MAX 虚拟矩阵（84 式占位列，与改键一致） */
export const DEEP80MAX_UI_KEYCODE_LAYOUT: number[][] = KEYBOARD_LAYOUTS[
  "deep80max"
] as number[][];

/**
 * 7180 固件/SDK 灯光网格键位（与改键紧凑矩阵一致，无 84 式占位列）
 */
export const XINGSHAN_7180_SDK_KEYCODE_LAYOUT: number[][] = [
  [
    41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 70, 72, 0, 0, 0, 0,
    0,
  ],
  [
    53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 75, 0, 0, 0, 0,
    0,
  ],
  [
    43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 78, 0, 0, 0, 0,
    0,
  ],
  [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0, 0, 0, 0, 0, 0, 0, 0],
  [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0, 0, 0],
  [
    224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
    0, 0,
  ],
];

export type LightingRowCol = { row: number; col: number };

type RowColMaps = {
  uiToSdk: Map<number, number>;
  sdkToUi: Map<number, number>;
};

const rowMapsCache: RowColMaps[] = [];

function buildRowColMaps(uiRow: number[], sdkRow: number[]): RowColMaps {
  const uiToSdk = new Map<number, number>();
  const sdkToUi = new Map<number, number>();

  const uiKeys = uiRow
    .map((keycode, col) => ({ keycode, col }))
    .filter((x) => x.keycode !== 0);
  const sdkKeys = sdkRow
    .map((keycode, col) => ({ keycode, col }))
    .filter((x) => x.keycode !== 0);

  let j = 0;
  for (const { keycode, col: uiCol } of uiKeys) {
    while (j < sdkKeys.length && sdkKeys[j].keycode !== keycode) {
      j++;
    }
    if (j < sdkKeys.length && sdkKeys[j].keycode === keycode) {
      const sdkCol = sdkKeys[j].col;
      uiToSdk.set(uiCol, sdkCol);
      sdkToUi.set(sdkCol, uiCol);
      j++;
    }
  }

  return { uiToSdk, sdkToUi };
}

function getRowMaps(row: number): RowColMaps {
  if (!rowMapsCache[row]) {
    rowMapsCache[row] = buildRowColMaps(
      DEEP80MAX_UI_KEYCODE_LAYOUT[row] ?? [],
      XINGSHAN_7180_SDK_KEYCODE_LAYOUT[row] ?? []
    );
  }
  return rowMapsCache[row];
}

/** Shift 行：SDK 紧凑列为 [LShift,Z,…]，UI 矩阵 LShift 后 col1 须为占位列 0 */
function normalizeDeep80MaxUiKeycodeRow(
  row: number,
  keyCodes: number[]
): number[] {
  if (row !== 4) return keyCodes;
  if (keyCodes[1] === 0) return keyCodes;
  if (keyCodes[0] === 225 && keyCodes[1] !== 0) {
    return [keyCodes[0], 0, ...keyCodes.slice(1)];
  }
  return keyCodes;
}

/**
 * 判断 SDK 返回的行是否已是 84 UI 矩阵（含占位列 0）。
 * 若已是 UI 格式仍走紧凑列 zip，会把 Z 写到 X 位、R-Shift 被 `,` 覆盖。
 */
function isDeep80MaxSdkRowAlreadyUiExpanded(
  row: number,
  sdkRow: number[]
): boolean {
  const template = DEEP80MAX_UI_KEYCODE_LAYOUT[row];
  if (!template?.length || !sdkRow?.length) return false;
  for (let i = 0; i < template.length; i++) {
    if (template[i] === 0 && (sdkRow[i] ?? 0) !== 0) {
      return false;
    }
  }
  return true;
}

/** UI 矩阵行 → SDK 紧凑列（与 expandDeep80MaxCompactRowToUi 互逆） */
function deep80MaxUiRowToCompact(row: number, uiRow: number[]): number[] {
  const compactLen = (XINGSHAN_7180_SDK_KEYCODE_LAYOUT[row] ?? []).length;
  const compact = Array.from({ length: compactLen }, () => 0);
  const { uiToSdk } = getRowMaps(row);
  for (let uiCol = 0; uiCol < uiRow.length; uiCol++) {
    const keycode = uiRow[uiCol];
    if (!keycode) continue;
    const sdkCol = uiToSdk.get(uiCol);
    if (sdkCol !== undefined) compact[sdkCol] = keycode;
  }
  return compact;
}

/** SDK 紧凑列 → 84 UI 矩阵行 */
function expandDeep80MaxCompactRowToUi(
  row: number,
  compactRow: number[]
): number[] {
  const uiRow = [...(DEEP80MAX_UI_KEYCODE_LAYOUT[row] ?? [])];
  const { sdkToUi } = getRowMaps(row);
  for (let sdkCol = 0; sdkCol < compactRow.length; sdkCol++) {
    const keycode = compactRow[sdkCol];
    if (!keycode) continue;
    const uiCol = sdkToUi.get(sdkCol);
    if (uiCol !== undefined && uiCol >= 0 && uiCol < uiRow.length) {
      uiRow[uiCol] = keycode;
    }
  }
  return normalizeDeep80MaxUiKeycodeRow(row, uiRow);
}

/** Shift 行错位特征：双 Z、R-Shift 被 `,` 占据等（旧版双重 expand 或错误缓存） */
function isDeep80MaxShiftRowCorrupt(uiRow: number[]): boolean {
  const expected = DEEP80MAX_UI_KEYCODE_LAYOUT[4] ?? [];
  if ((uiRow[1] ?? 0) !== 0) return true;
  if (uiRow[2] === 29 && uiRow[3] === 29) return true;
  if (expected[12] === 229 && uiRow[12] !== 229) return true;
  return false;
}

/**
 * 任意 SDK/缓存行 → 规范 84 UI 矩阵（改键、虚拟键盘、布局缓存统一入口）
 */
export function sanitizeDeep80MaxLayoutKeycodeRow(
  row: number,
  sdkRow: number[]
): number[] {
  const template = DEEP80MAX_UI_KEYCODE_LAYOUT[row] ?? [];
  if (!sdkRow?.length) return [...template];

  const compact = isDeep80MaxSdkRowAlreadyUiExpanded(row, sdkRow)
    ? deep80MaxUiRowToCompact(row, sdkRow)
    : [...sdkRow];

  let uiRow = expandDeep80MaxCompactRowToUi(row, compact);

  if (row === 4 && isDeep80MaxShiftRowCorrupt(uiRow)) {
    uiRow = [...(DEEP80MAX_UI_KEYCODE_LAYOUT[4] ?? [])];
  }

  if (uiRow.length < template.length) {
    return [...uiRow, ...Array(template.length - uiRow.length).fill(0)];
  }
  return uiRow;
}

/**
 * Shift 行（row4）：SDK 紧凑列与 84 UI 占位列对齐（L-Shift 后 col1 为占位 0，
 * SDK col1=Z 对应 UI col2）。键码 zip 映射见 buildRowColMaps；此处在此基础上补 R-Shift/↑ 第二颗灯珠列。
 */
function resolveShiftRowUiToSdkCols(col: number): number[] {
  const mapped = getRowMaps(4).uiToSdk.get(col);
  const cols: number[] = mapped !== undefined ? [mapped] : [];
  if (col === 12) {
    [12, 13].forEach((c) => {
      if (!cols.includes(c)) cols.push(c);
    });
  }
  if (col === 13) {
    [12, 13].forEach((c) => {
      if (!cols.includes(c)) cols.push(c);
    });
  }
  if (col === 14) {
    if (!cols.includes(14)) cols.push(14);
  }
  if (col === 15) {
    if (!cols.includes(15)) cols.push(15);
  }
  return cols;
}

function resolveShiftRowSdkToUiCol(sdkCol: number): number | undefined {
  const uiCol = getRowMaps(4).sdkToUi.get(sdkCol);
  if (uiCol !== undefined) return uiCol;
  if (sdkCol === 12 || sdkCol === 13) return 13;
  if (sdkCol === 14 || sdkCol === 15) return 14;
  return undefined;
}

/**
 * 校准/ADC Shift 行：与灯位同坐标系（LShift 占 sdk0/1，Z 起与 UI 同列），
 * 不可用键码紧凑 zip（否则 UI 对齐的 Z@col2 会被映到 X@col3）。
 */
function resolveDeep80MaxShiftCalibrationSdkToUiCol(
  sdkCol: number
): number | undefined {
  if (sdkCol === 0 || sdkCol === 1) return 0;
  if (sdkCol >= 2 && sdkCol <= 11) return sdkCol;
  if (sdkCol === 12 || sdkCol === 13) return 12;
  if (sdkCol === 14 || sdkCol === 15) return 15;
  return undefined;
}

/** 虚拟键盘分裂空格 UI 列（占位列 3/4/5/7/8，Space 在 col 6） */
export const DEEP80MAX_SPACE_LED_UI_COLS = [3, 4, 5, 7, 8] as const;

/** 固件/SDK 实际灯珠列（col 6 同时为 Space 键位，与 ES87 一致） */
export const DEEP80MAX_SPACE_LED_SDK_COLS = [4, 5, 6, 7, 8] as const;

/** @deprecated 使用 DEEP80MAX_SPACE_LED_UI_COLS */
export const DEEP80MAX_SPACE_LED_MATRIX_COLS = DEEP80MAX_SPACE_LED_UI_COLS;

const DEEP80MAX_SPACE_UI_TO_SDK = new Map<number, number>(
  DEEP80MAX_SPACE_LED_UI_COLS.map((uiCol, i) => [
    uiCol,
    DEEP80MAX_SPACE_LED_SDK_COLS[i]!,
  ])
);

const DEEP80MAX_SPACE_SDK_TO_UI = new Map<number, number>(
  DEEP80MAX_SPACE_LED_SDK_COLS.map((sdkCol, i) => [
    sdkCol,
    DEEP80MAX_SPACE_LED_UI_COLS[i]!,
  ])
);

/** 底行分裂空格：UI 占位列 → SDK 灯珠列 */
function resolveSpaceRowUiToSdkCols(col: number): number[] {
  const sdkCol = DEEP80MAX_SPACE_UI_TO_SDK.get(col);
  if (sdkCol !== undefined) return [sdkCol];
  if (col === 6) return [...DEEP80MAX_SPACE_LED_SDK_COLS];
  return [];
}

function resolveSpaceRowSdkToUiCol(sdkCol: number): number | undefined {
  return DEEP80MAX_SPACE_SDK_TO_UI.get(sdkCol);
}

/** UI (row,col) → SDK 性能/灯光矩阵列（触发 API 取首列） */
export function resolveDeep80MaxUiToSdkCol(
  row: number,
  col: number
): number | undefined {
  const cols = resolveDeep80MaxUiToSdkCols(row, col);
  return cols[0];
}

/** UI (row,col) → SDK 灯光格（Enter 等可能对应多格） */
export function resolveDeep80MaxUiToSdkCols(
  row: number,
  col: number
): number[] {
  if (row === 5) {
    const spaceCols = resolveSpaceRowUiToSdkCols(col);
    if (spaceCols.length) return spaceCols;
  }
  if (row === 4) {
    return resolveShiftRowUiToSdkCols(col);
  }

  const sdkCol = getRowMaps(row).uiToSdk.get(col);
  const cols: number[] = [];
  if (sdkCol !== undefined) {
    cols.push(sdkCol);
  }
  // Enter：84 矩阵在 col13，固件 Enter 在 col12，部分灯珠在 col13
  if (row === 3 && col === 13) {
    if (!cols.includes(12)) cols.push(12);
    if (!cols.includes(13)) cols.push(13);
  }
  return cols;
}

/** SDK (row,col) → UI 矩阵列（用于回显 / 自定义角标） */
export function resolveDeep80MaxSdkToUiCol(
  row: number,
  sdkCol: number
): number | undefined {
  if (row === 5) {
    const uiCol = resolveSpaceRowSdkToUiCol(sdkCol);
    if (uiCol !== undefined) return uiCol;
  }
  if (row === 4) {
    return resolveShiftRowSdkToUiCol(sdkCol);
  }

  const uiCol = getRowMaps(row).sdkToUi.get(sdkCol);
  if (uiCol !== undefined) return uiCol;
  if (row === 3 && sdkCol === 13) return 13;
  return undefined;
}

function mergeCalibrationScalar(
  existing: number | undefined,
  incoming: number
): number {
  const a = Number(existing) || 0;
  const b = Number(incoming) || 0;
  return Math.max(a, b);
}

/** SDK 行校准/ADC 数据 → 84 UI 矩阵列（与灯光/触发页列映射一致） */
export function expandDeep80MaxSdkCalibrationRowToUi(
  row: number,
  sdkRow: {
    adc?: number[];
    route?: number[];
    status?: number[];
    performance?: unknown[];
  }
): {
  adc: number[];
  route: number[];
  status: number[];
  performance: unknown[];
} {
  const uiLen = DEEP80MAX_UI_KEYCODE_LAYOUT[row]?.length ?? 21;
  const adc = Array.from({ length: uiLen }, () => 0);
  const route = Array.from({ length: uiLen }, () => 0);
  const status = Array.from({ length: uiLen }, () => 0);
  const performance: unknown[] = Array.from({ length: uiLen }, () => 0);

  const sdkLen = Math.max(
    sdkRow.adc?.length ?? 0,
    sdkRow.route?.length ?? 0,
    sdkRow.status?.length ?? 0,
    sdkRow.performance?.length ?? 0
  );

  for (let sdkCol = 0; sdkCol < sdkLen; sdkCol++) {
    const uiCol =
      row === 4
        ? resolveDeep80MaxShiftCalibrationSdkToUiCol(sdkCol)
        : resolveDeep80MaxSdkToUiCol(row, sdkCol);
    if (uiCol === undefined || uiCol < 0 || uiCol >= uiLen) continue;

    if (sdkRow.adc?.[sdkCol] != null) {
      adc[uiCol] = mergeCalibrationScalar(adc[uiCol], sdkRow.adc[sdkCol]);
    }
    if (sdkRow.route?.[sdkCol] != null) {
      route[uiCol] = mergeCalibrationScalar(route[uiCol], sdkRow.route[sdkCol]);
    }
    if (sdkRow.status?.[sdkCol] != null) {
      status[uiCol] = mergeCalibrationScalar(
        status[uiCol],
        sdkRow.status[sdkCol]
      );
    }
    if (sdkRow.performance?.[sdkCol] != null) {
      performance[uiCol] = sdkRow.performance[sdkCol];
    }
  }

  // 底行：SDK col6 为空格键位，分裂灯映射到 col3–5/7–8；虚拟键盘 Space 在 UI col6，须单独回显校准数据
  if (row === 5 && sdkLen > 6) {
    const spaceUiCol = 6;
    if (spaceUiCol < uiLen) {
      if (sdkRow.adc?.[6] != null) {
        adc[spaceUiCol] = mergeCalibrationScalar(
          adc[spaceUiCol],
          sdkRow.adc[6]
        );
      }
      if (sdkRow.route?.[6] != null) {
        route[spaceUiCol] = mergeCalibrationScalar(
          route[spaceUiCol],
          sdkRow.route[6]
        );
      }
      if (sdkRow.status?.[6] != null) {
        status[spaceUiCol] = mergeCalibrationScalar(
          status[spaceUiCol],
          sdkRow.status[6]
        );
      }
      if (sdkRow.performance?.[6] != null) {
        performance[spaceUiCol] = sdkRow.performance[6];
      }
    }
  }

  return { adc, route, status, performance };
}

/** SDK 行键码 → 84 UI 矩阵行（改键回显；空格保持 col6 单键，不用灯光分裂列映射） */
export function expandDeep80MaxSdkKeycodeRowToUi(
  row: number,
  sdkRow: number[]
): number[] {
  return sanitizeDeep80MaxLayoutKeycodeRow(row, sdkRow);
}

/** 改键/性能等：UI 矩阵列 → SDK 紧凑列（与 expandDeep80MaxSdkKeycodeRowToUi 互逆） */
export function resolveDeep80MaxRemappingUiToSdkCol(
  row: number,
  col: number
): number | undefined {
  return getRowMaps(row).uiToSdk.get(col);
}

const emptyLightingCell = () => ({
  R: 0,
  G: 0,
  B: 0,
  isCustom: false,
});

/** SDK 网格 → UI 网格（供 virtual keyboard 取色） */
export function expandDeep80MaxSdkLightingGridToUi<
  T extends Record<string, unknown>,
>(sdkGrid: T[][]): T[][] {
  return sdkGrid.map((sdkRow, row) => {
    const uiLen = DEEP80MAX_UI_KEYCODE_LAYOUT[row]?.length ?? 21;
    const uiRow = Array.from(
      { length: uiLen },
      () => JSON.parse(JSON.stringify(emptyLightingCell())) as T
    );

    sdkRow?.forEach((cell, sdkCol) => {
      const uiCol = resolveDeep80MaxSdkToUiCol(row, sdkCol);
      if (uiCol !== undefined && cell != null) {
        uiRow[uiCol] = JSON.parse(JSON.stringify(cell)) as T;
      }
    });

    // Enter 第二颗灯珠在 SDK col13，同步到 UI 占位列 12
    if (row === 3 && sdkRow?.[13] != null && uiLen > 12) {
      uiRow[12] = JSON.parse(JSON.stringify(sdkRow[13])) as T;
    }

    // Shift 行：R-Shift / ↑ 第二颗灯珠同步到 UI 占位列 13 / 14
    if (row === 4) {
      if (sdkRow?.[13] != null && uiLen > 13) {
        uiRow[13] = JSON.parse(JSON.stringify(sdkRow[13])) as T;
      }
      if (sdkRow?.[14] != null && uiLen > 14) {
        uiRow[14] = JSON.parse(JSON.stringify(sdkRow[14])) as T;
      }
    }

    // 底行：空格 5 灯 SDK 列 4–8 → UI 占位列 3/4/5/7/8
    if (row === 5) {
      DEEP80MAX_SPACE_LED_SDK_COLS.forEach((sdkCol) => {
        const uiCol = DEEP80MAX_SPACE_SDK_TO_UI.get(sdkCol);
        if (uiCol == null) return;
        if (sdkRow?.[sdkCol] != null && uiCol >= 0 && uiCol < uiLen) {
          uiRow[uiCol] = JSON.parse(JSON.stringify(sdkRow[sdkCol])) as T;
        }
      });
    }

    return uiRow;
  });
}

/** 将 UI 选中键写入 SDK 灯光网格 */
export function applyDeep80MaxUiLightingToSdkGrid(
  sdkGrid: Array<Array<{ R: number; G: number; B: number; isCustom: boolean }>>,
  selections: LightingRowCol[],
  rgba: { R: number; G: number; B: number }
): void {
  selections.forEach(({ row, col }) => {
    const sdkCols = resolveDeep80MaxUiToSdkCols(row, col);
    const sdkRow = sdkGrid[row];
    if (!sdkRow) return;
    sdkCols.forEach((sdkCol) => {
      if (sdkRow[sdkCol] == null) return;
      sdkRow[sdkCol].R = rgba.R;
      sdkRow[sdkCol].G = rgba.G;
      sdkRow[sdkCol].B = rgba.B;
      sdkRow[sdkCol].isCustom = true;
    });
  });
}

export function clearDeep80MaxUiLightingOnSdkGrid(
  sdkGrid: Array<Array<{ isCustom: boolean }>>,
  row: number,
  col: number
): void {
  const sdkCols = resolveDeep80MaxUiToSdkCols(row, col);
  const sdkRow = sdkGrid[row];
  if (!sdkRow) return;
  sdkCols.forEach((sdkCol) => {
    if (sdkRow[sdkCol]) sdkRow[sdkCol].isCustom = false;
  });
}

/** 从 SDK 网格提取 UI 行列的自定义灯位（供角标 / uuid 映射） */
export function extractDeep80MaxCustomKeyPositionsFromSdk(
  sdkGrid: Array<Array<{ isCustom?: boolean }>>
): LightingRowCol[] {
  const positions: LightingRowCol[] = [];
  sdkGrid.forEach((row, rowIndex) => {
    row?.forEach((cell, sdkCol) => {
      if (!cell?.isCustom) return;
      const uiCol = resolveDeep80MaxSdkToUiCol(rowIndex, sdkCol);
      if (uiCol === undefined) return;
      const exists = positions.some(
        (p) => p.row === rowIndex && p.col === uiCol
      );
      if (!exists) positions.push({ row: rowIndex, col: uiCol });
    });
  });
  return positions;
}

/**
 * Deep80 Max / DK80 等 `keyboardType=deep80max` 星闪设备族（VID 7334）。
 * - 7180 Deep80 Max：SDK 灯光/性能矩阵为紧凑列（无 Esc、L-Shift 后占位）
 * - 7229 DK80：固件网格与 84 UI 对齐（含占位列），勿走紧凑映射
 */
export function isXingshanDeep80MaxDevice(
  productId?: number | string,
  vendorId?: number | string
): boolean {
  return (
    Number(vendorId) === 7334 &&
    (Number(productId) === 7180 || Number(productId) === 7229)
  );
}

/** 仅 Deep80 Max (7180)：需 UI→SDK 压缩映射。DK80 (7229) 禁止使用。 */
export function usesDeep80MaxCompactSdkGrid(
  productId?: number | string,
  vendorId?: number | string
): boolean {
  return Number(vendorId) === 7334 && Number(productId) === 7180;
}

/** DK80 (7229)：F/字母/Shift 与 UI 同列；仅底行空格 5 灯需 UI[3,4,5,7,8]→SDK[4,5,6,7,8] */
export function isXingshanDk80Device(
  productId?: number | string,
  vendorId?: number | string
): boolean {
  return Number(vendorId) === 7334 && Number(productId) === 7229;
}

export function resolveDk80UiToSdkCols(row: number, col: number): number[] {
  if (row === 5) {
    const spaceCols = resolveSpaceRowUiToSdkCols(col);
    if (spaceCols.length) return spaceCols;
  }
  return [col];
}

export function applyDk80UiLightingToSdkGrid(
  sdkGrid: Array<Array<{ R: number; G: number; B: number; isCustom: boolean }>>,
  selections: LightingRowCol[],
  rgba: { R: number; G: number; B: number }
): void {
  selections.forEach(({ row, col }) => {
    const sdkCols = resolveDk80UiToSdkCols(row, col);
    const sdkRow = sdkGrid[row];
    if (!sdkRow) return;
    sdkCols.forEach((sdkCol) => {
      if (sdkRow[sdkCol] == null) return;
      sdkRow[sdkCol].R = rgba.R;
      sdkRow[sdkCol].G = rgba.G;
      sdkRow[sdkCol].B = rgba.B;
      sdkRow[sdkCol].isCustom = true;
    });
  });
}

export function clearDk80UiLightingOnSdkGrid(
  sdkGrid: Array<Array<{ isCustom: boolean }>>,
  row: number,
  col: number
): void {
  const sdkCols = resolveDk80UiToSdkCols(row, col);
  const sdkRow = sdkGrid[row];
  if (!sdkRow) return;
  sdkCols.forEach((sdkCol) => {
    if (sdkRow[sdkCol]) sdkRow[sdkCol].isCustom = false;
  });
}

/** SDK 网格 → UI：非空格直拷；底行空格 SDK[4–8]→UI[3/4/5/7/8] */
export function expandDk80SdkLightingGridToUi<
  T extends Record<string, unknown>,
>(sdkGrid: T[][]): T[][] {
  return sdkGrid.map((sdkRow, row) => {
    const uiLen = Math.max(
      sdkRow?.length ?? 0,
      DEEP80MAX_UI_KEYCODE_LAYOUT[row]?.length ?? 21
    );
    const uiRow = Array.from({ length: uiLen }, (_, i) => {
      const cell = sdkRow?.[i];
      return cell != null
        ? (JSON.parse(JSON.stringify(cell)) as T)
        : (JSON.parse(JSON.stringify(emptyLightingCell())) as T);
    });

    if (row === 5) {
      DEEP80MAX_SPACE_LED_SDK_COLS.forEach((sdkCol) => {
        const uiCol = DEEP80MAX_SPACE_SDK_TO_UI.get(sdkCol);
        if (uiCol == null) return;
        if (sdkRow?.[sdkCol] != null && uiCol >= 0 && uiCol < uiLen) {
          uiRow[uiCol] = JSON.parse(JSON.stringify(sdkRow[sdkCol])) as T;
        }
      });
    }

    return uiRow;
  });
}

export function extractDk80CustomKeyPositionsFromSdk(
  sdkGrid: Array<Array<{ isCustom?: boolean }>>
): LightingRowCol[] {
  const positions: LightingRowCol[] = [];
  sdkGrid.forEach((row, rowIndex) => {
    row?.forEach((cell, sdkCol) => {
      if (!cell?.isCustom) return;
      let uiCol = sdkCol;
      if (rowIndex === 5) {
        const mapped = resolveSpaceRowSdkToUiCol(sdkCol);
        if (mapped !== undefined) uiCol = mapped;
      }
      const exists = positions.some(
        (p) => p.row === rowIndex && p.col === uiCol
      );
      if (!exists) positions.push({ row: rowIndex, col: uiCol });
    });
  });
  return positions;
}
